import { ref } from 'vue';
import { defineStore } from 'pinia';
import { extractDocument, getExtractionAvailabilityStatus } from '@/api/documents/aiDocumentsApi.ts';
import { removeS3Upload, uploadToS3 } from '@/api/documents/documentsApi.ts';
import type { S3Info } from '@/api/documents/types.ts';
import { throwableCall } from '@/api/lib/integration.ts';
import { prettyPrintSize } from '@/application/utils/prettyPrintSize.ts';
import { MAX_UPLOAD_SIZE } from '@/documents/composables/documentUpload.ts';
import { captureException } from '@sentry/vue';
import { useTagManager } from '@demv_systems/feu-tag-manager';
import { TrackEventCategory } from '@/application/utils/tracking/events.ts';
import type { ContractData } from '@/contracts/types/types.ts';
import { ExtractDocumentResponseSchema, type ParsedExtractDocumentResponse } from '@/api/documents/schemas.ts';
import type { RequestOptions } from '@/api/lib/types.ts';

export const allowedContentTypes = ['application/pdf', 'image/jpeg', 'image/png']

export enum DOCUMENT_EXTRACTION_STEP {
  INITIAL = 'initial',
  UPLOAD = 'upload',
  EXTRACT = 'extract',
}

const getInitialExtract = () => ({
  vertragsnummer: null,
  beginn: null,
  ablauf: null,
  beitrag: null,
  zahlweise: null,
  contractId: null,
  gesellschaftId: null,
  sparteId: null,
})

export const useAiDocumentExtractionStore = defineStore('aiDocumentExtraction', () => {
  const step = ref<DOCUMENT_EXTRACTION_STEP>(DOCUMENT_EXTRACTION_STEP.INITIAL);
  const uploadedFiles = ref<Map<number, string>>(new Map());
  const invalidFiles = ref<File[]>([]);
  const fileNames = ref<string[]>([]);
  const extract = ref<ParsedExtractDocumentResponse>(getInitialExtract());
  const hasExtract = ref(false);
  const errorMessages = ref<string[]>([])
  const uploadError = ref<string | null>(null)
  const extractError = ref<string | null>(null)
  const s3info = ref<S3Info[]>([]);
  const aiIsAvailable = ref(true);
  const aiCheckIsLoading = ref(false);
  const extractAbortController = ref<AbortController | null>(null);

  const validate = (file: File) => file.size <= MAX_UPLOAD_SIZE && allowedContentTypes
    .some((allowedContentType) => file.type.includes(allowedContentType))

  function $reset() {
    step.value = DOCUMENT_EXTRACTION_STEP.INITIAL;
    uploadedFiles.value.clear();
    invalidFiles.value = [];
    fileNames.value = [];
    extract.value = getInitialExtract();
    hasExtract.value = false;
    errorMessages.value = [];
    uploadError.value = null;
    extractError.value = null;
    s3info.value = [];
    aiIsAvailable.value = true;
    aiCheckIsLoading.value = false;
    extractAbortController.value?.abort();
    extractAbortController.value = null;
  }

  async function uploadFiles(files: FileList) {
    step.value = DOCUMENT_EXTRACTION_STEP.UPLOAD;
    invalidFiles.value = [];
    errorMessages.value = [];

    if (files.length === 0) return;

    const totalSize = Array.from(files).reduce((acc, curr) => acc + curr.size, 0);
    if (totalSize > MAX_UPLOAD_SIZE) {
      errorMessages.value.push("Die Dateien sind zusammen größer als " + prettyPrintSize(MAX_UPLOAD_SIZE));
      return;
    }

    try {
      const s3infoRes = await Promise.all(
         Array.from(files).map(async (file) => {
           if (!validate(file)) {
             invalidFiles.value.push(file);
             return undefined;
           } else {
             const uploadRes = await uploadToS3(file);
             uploadedFiles.value.set(uploadRes.id, file.name);
             return uploadRes;
           }
         })
       ).then(results => results.filter((res): res is S3Info => res !== undefined));

      s3info.value = [ ...s3info.value, ...s3infoRes ];
    } catch (err) {
      captureException(err)
      uploadError.value = 'Es gab einen Fehler beim Hochladen von Dateien.';
    }
    finally {
      step.value = DOCUMENT_EXTRACTION_STEP.INITIAL;
    }
  }

  async function removeUpload(upload: number) {
    step.value = DOCUMENT_EXTRACTION_STEP.INITIAL;
    uploadedFiles.value.delete(upload);
    s3info.value = s3info.value.filter((info) => info.id !== upload);
    await removeS3Upload({ upload })
  }

  async function extractDocuments() {
    step.value = DOCUMENT_EXTRACTION_STEP.EXTRACT;
    extractAbortController.value = new AbortController();
    try {
      const options: RequestOptions = { signal: extractAbortController.value.signal };
      const data = await throwableCall(extractDocument(options)( { uploadIds: Array.from(uploadedFiles.value.keys()) }))
      extract.value = ExtractDocumentResponseSchema.parse(data);
      useTagManager().push({ event: TrackEventCategory.AI_CONTRACT_EXTRACTION, documentExtractionCount: uploadedFiles.value.size })
      hasExtract.value = true;
    } catch (err) {
      captureException(err);
      extractError.value = 'Es gab einen Fehler beim Auslesen der Dokumente.';
    } finally {
      step.value = DOCUMENT_EXTRACTION_STEP.INITIAL;
      extractAbortController.value = null;
    }
  }

  function cancelExtraction() {
    if (extractAbortController.value) {
      extractAbortController.value.abort();
    }
  }

  async function compareExtractionWithForm(form: ContractData) {
    const differences: Record<string, { extract: string|number|null; form: string|number|null }> = {};

    const fieldsToCompare: [
      keyof Omit<ParsedExtractDocumentResponse, 'contractId'>,
      keyof Omit<ContractData, 'kennzeichen' | 'notiz'>
    ][] = [
      ['sparteId', 'sparte_id'],
      ['gesellschaftId', 'gesellschaft_id'],
      ['vertragsnummer', 'vertragsnummer'],
      ['beginn', 'beginn'],
      ['ablauf', 'ablaufdatum'],
      ['beitrag', 'beitrag'],
      ['zahlweise', 'zahlweise'],
    ];

    for (const [extractKey, formKey] of fieldsToCompare) {
      const extractValue = extract.value[extractKey];
      const formValue = form[formKey];

      if (extractValue !== formValue) {
        differences[extractKey] = { extract: extractValue, form: formValue };
      }
    }

    return differences;
  }

  async function fetchExtractionAvailabilityStatus() {
    aiCheckIsLoading.value = true;
    try {
      const { enabled } = await throwableCall(getExtractionAvailabilityStatus());
      aiIsAvailable.value = enabled;
    } catch (err) {
      captureException(err);
    } finally {
      aiCheckIsLoading.value = false;
    }
  }

  return {
    step,
    s3info,
    extract,
    fileNames,
    hasExtract,
    uploadError,
    invalidFiles,
    extractError,
    uploadedFiles,
    errorMessages,
    aiIsAvailable,
    aiCheckIsLoading,
    $reset,
    uploadFiles,
    removeUpload,
    extractDocuments,
    cancelExtraction,
    compareExtractionWithForm,
    fetchExtractionAvailabilityStatus,
  };
});
